/**
 * Microsoft Clarity for the public marketing site.
 * Project z489ujni15. Loads on primedocumentsign.com only, unless this browser opts in.
 * Never throws. Never sends personal data, click-id values, or document contents.
 */

const CLARITY_PROJECT_ID = 'z489ujni15'
const ATTR_KEY = 'pds_clarity_attr'
const INIT_FLAG = '__pdsClarityInit'

const STAGE_RANK = {
  visitor: 0,
  pricing_viewed: 1,
  features_viewed: 1,
  faq_viewed: 1,
  contact_viewed: 1,
  trial_clicked: 2,
  gift_signup: 2,
  signup_form: 3,
  validation_error: 3,
  account_created: 4,
  logged_in: 4,
  plan_step: 5,
  plan_selected: 5,
  checkout_started: 6,
  trial_active: 7,
}

const onceKeys = new Set()
let signupStarted = false
let contactStarted = false
let stageRank = 0

function storageFlag(value) {
  try {
    return window.localStorage?.getItem('pds_clarity') === value
  } catch (_) {
    return false
  }
}

function clarityEnabled() {
  if (typeof window === 'undefined') return false
  if (storageFlag('0')) return false
  const host = window.location.hostname
  const onProduction = host === 'primedocumentsign.com' || host === 'www.primedocumentsign.com'
  const devOn = String(import.meta.env.VITE_CLARITY_ENABLE_IN_DEV || '').trim() === 'true'
  return onProduction || devOn || storageFlag('1')
}

export function clarityProjectId() {
  return clarityEnabled() ? CLARITY_PROJECT_ID : ''
}

function callClarity(method, ...args) {
  try {
    if (typeof window === 'undefined' || typeof window.clarity !== 'function') return
    window.clarity(method, ...args)
  } catch (_) {
    /* Ad blockers and CSP failures must not affect signup, login, or checkout. */
  }
}

export function safeClarityValue(value, max = 64) {
  const raw = String(value ?? '').trim().toLowerCase()
  if (!raw || raw.includes('@') || raw.includes('://')) return ''
  const cleaned = raw.replace(/[^a-z0-9._:/-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')
  return cleaned.slice(0, max)
}

export function claritySafeTier(value) {
  const tier = safeClarityValue(value, 20)
  if (tier === 'basic' || tier === 'standard' || tier === 'premium') return tier
  return 'unknown'
}

export function claritySafeBilling(value) {
  const billing = safeClarityValue(value, 20)
  if (billing === 'yearly' || billing === 'annual') return 'annual'
  if (billing === 'monthly') return 'monthly'
  return 'unknown'
}

function safePath(pathname) {
  const path = String(pathname || '/').split('?')[0].split('#')[0]
  const cleaned = path.replace(/[^a-zA-Z0-9/_-]+/g, '').slice(0, 120)
  return cleaned || '/'
}

function deviceCategory() {
  try {
    const ua = navigator.userAgent || ''
    const width = window.innerWidth || 0
    if (/ipad|tablet|playbook|silk/i.test(ua)) return 'tablet'
    if (/iphone|ipod|android.+mobile|mobile/i.test(ua) || (width > 0 && width < 768)) return 'mobile'
    if (width >= 768 && width < 1024) return 'tablet'
    return 'desktop'
  } catch (_) {
    return 'unknown'
  }
}

function referrerHost() {
  try {
    return new URL(document.referrer || '').hostname.replace(/^www\./, '')
  } catch (_) {
    return ''
  }
}

function classifySource(attr) {
  const source = attr.utm_source
  const medium = attr.utm_medium
  if (attr.has_gclid || source === 'google_ads' || (source === 'google' && (medium === 'cpc' || medium === 'ppc' || medium === 'paid'))) {
    return 'google_ads'
  }
  if (attr.has_reddit_click || source === 'reddit' || source === 'reddit_ads' || source.includes('reddit')) return 'reddit_ads'
  if (attr.has_msclkid || source === 'bing') return 'bing_ads'
  if (source) return source
  const host = referrerHost()
  if (host.includes('google.')) return 'google_organic'
  if (host) return 'referral'
  return 'direct'
}

function captureAttribution() {
  if (typeof window === 'undefined') return null
  try {
    const existing = JSON.parse(sessionStorage.getItem(ATTR_KEY) || 'null')
    if (existing && existing.landing_page) return existing
  } catch (_) {
    /* Ignore unreadable storage and recapture. */
  }
  let params
  try {
    params = new URLSearchParams(window.location.search)
  } catch (_) {
    return null
  }
  const attr = {
    utm_source: safeClarityValue(params.get('utm_source')),
    utm_medium: safeClarityValue(params.get('utm_medium')),
    utm_campaign: safeClarityValue(params.get('utm_campaign')),
    utm_content: safeClarityValue(params.get('utm_content')),
    utm_term: safeClarityValue(params.get('utm_term')),
    has_gclid: Boolean(params.get('gclid') || params.get('gbraid') || params.get('wbraid') || params.get('gad_source')),
    has_reddit_click: Boolean(params.get('rdt_cid') || params.get('rdt_uid')),
    has_msclkid: Boolean(params.get('msclkid')),
    landing_page: safePath(window.location.pathname),
    device: deviceCategory(),
  }
  attr.traffic_source = classifySource(attr)
  try {
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr))
  } catch (_) {
    /* Storage can be blocked. Tags below still apply for this page view. */
  }
  return attr
}

function applyAttributionTags() {
  const attr = captureAttribution()
  if (!attr) return
  setClarityTag('traffic_source', attr.traffic_source)
  setClarityTag('campaign', attr.utm_campaign || 'none')
  setClarityTag('medium', attr.utm_medium || 'none')
  setClarityTag('landing_page', attr.landing_page || '/')
  setClarityTag('device', attr.device || 'unknown')
  if (attr.has_gclid) setClarityTag('ads_click', 'google')
  else if (attr.has_reddit_click) setClarityTag('ads_click', 'reddit')
  else if (attr.has_msclkid) setClarityTag('ads_click', 'bing')
  if (stageRank === 0) setSignupStage('visitor')
}

export function initClarity() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false
  if (window[INIT_FLAG]) return true
  const projectId = clarityProjectId()
  if (!projectId) return false
  window[INIT_FLAG] = true
  try {
    ;(function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) }
      t = l.createElement(r)
      t.async = 1
      t.src = 'https://www.clarity.ms/tag/' + i
      y = l.getElementsByTagName(r)[0]
      if (y && y.parentNode) y.parentNode.insertBefore(t, y)
      else l.head.appendChild(t)
    })(window, document, 'clarity', 'script', projectId)
  } catch (_) {
    window[INIT_FLAG] = false
    return false
  }
  applyAttributionTags()
  return true
}

export function stopClarity() {
  callClarity('stop')
}

export function setClarityTag(key, value) {
  const safeKey = safeClarityValue(key, 40)
  const safeValue = safeClarityValue(value, 80)
  if (!safeKey || !safeValue) return
  callClarity('set', safeKey, safeValue)
}

export function setSignupStage(stage) {
  const name = safeClarityValue(stage, 40)
  const rank = STAGE_RANK[name]
  if (rank == null || rank < stageRank) return
  stageRank = rank
  setClarityTag('signup_stage', name)
}

export function setClarityPage(pathname) {
  const path = safePath(pathname)
  setClarityTag('page', path)
}

export function trackClarityEvent(name) {
  const eventName = safeClarityValue(name, 60)
  if (!eventName) return
  callClarity('event', eventName)
}

export function trackClarityEventOnce(name) {
  const eventName = safeClarityValue(name, 60)
  if (!eventName || onceKeys.has(eventName)) return
  onceKeys.add(eventName)
  trackClarityEvent(eventName)
}

export function trackSignupStarted() {
  if (signupStarted) return
  signupStarted = true
  setSignupStage('signup_form')
  trackClarityEvent('signup_started')
}

export function trackContactFormStarted() {
  if (contactStarted) return
  contactStarted = true
  trackClarityEvent('contact_form_started')
}

export function trackSignupValidation(category) {
  setSignupStage('validation_error')
  setClarityTag('signup_error', safeClarityValue(category, 40) || 'validation')
  trackClarityEventOnce('signup_validation_error')
}

export function clarityAuthFailureCategory(err) {
  const code = String(err?.code || '')
  if (code === 'auth/email-already-in-use') return 'email_in_use'
  if (code === 'auth/invalid-email') return 'invalid_email'
  if (code === 'auth/weak-password') return 'weak_password'
  if (code === 'auth/wrong-password' || code === 'auth/invalid-credential' || code === 'auth/user-not-found') return 'invalid_credential'
  if (code === 'auth/network-request-failed') return 'network'
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return 'popup_closed'
  if (code === 'auth/too-many-requests') return 'rate_limited'
  return 'auth_error'
}

export function clarityCheckoutFailureCategory(err) {
  const code = String(err?.code || '').toLowerCase()
  if (code.includes('unauthenticated')) return 'signed_out'
  if (code.includes('unavailable') || code.includes('deadline') || code.includes('network')) return 'network'
  const message = String(err?.message || '').toLowerCase()
  if (message.includes('network') || message.includes('failed to fetch')) return 'network'
  return 'checkout_error'
}
